import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import ResetPassword from './ResetPassword';
import StudentSchedule from './StudentSchedule';
import StudentPaidAvailability from './StudentPaidAvailability';
import StudentClasses from './StudentClasses';
import Certificates from './Certificates';
import PTM from './StudentPtm';

import { format, parseISO, addWeeks } from 'date-fns';

import './StudentProfile.css';

const StudentProfile = () => {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [assignedTeacher, setAssignedTeacher] = useState(null);
  const [sendCongrats, setSendCongrats] = useState(false);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [updatingToPaid, setUpdatingToPaid] = useState(false);
  const isDemoStudent = id.includes('demo');
  
  // Payment related states
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentPayment, setCurrentPayment] = useState({
    Id: null,
    IdStudent: id,
    DateTimeOfPayement: '',
    Check: 0,
    Amount: 0
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('userData');
        const headers = { 'Authorization': `Bearer ${token}` };

        if (isDemoStudent) {
          const studentRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admin/studentprofile/demostudent/${id}`,
            { headers }
          );
          setStudent(studentRes.data);
        } else {
          const [studentRes, teachersRes, assignedRes] = await Promise.all([
            axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/admin/studentprofile/paidstudent/${id}`,
              { headers }
            ),
            axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/admin/studentprofile/teachers/list`,
              { headers }
            ),
            axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/admin/studentprofile/teacherassigned/${id}`,
              { headers }
            )
          ]);

          setStudent(studentRes.data);
          setTeachers(teachersRes.data);
          setAssignedTeacher(assignedRes.data[0]?.IdTeacher || '');
          
          // Fetch payments for paid students
          const paymentsRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admin/student-payments/${id}`,
            { headers }
          );
          setPayments(paymentsRes.data);
        }

        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isDemoStudent]);

  const handleAssignTeacher = async () => {
    try {
      setTeacherLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/studentprofile/assignteacher/${id}`,
        { 
          teacherId: assignedTeacher,
          sendCongrats 
        },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('userData')}` } }
      );

      setAssignedTeacher(response.data.teacherId);
      alert('Teacher assigned successfully!');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setTeacherLoading(false);
    }
  };

  const sendWhatsAppMessage = () => {
    const message = `Welcome ${student.FirstName} ${student.LastName}, this is your credentials for accessing the dashboard.\nUsername: ${student.Username}\nPassword: ${student.CodeJoinDemo || '123456aA@'}\nHappy learning from RoboMind Academy!`;
    window.open(`https://api.whatsapp.com/send?phone=${student.MobileNumber}&text=${encodeURIComponent(message)}`, '_blank');
  };

  const sendDemoInvitation = () => {
    const message = `Hello ${student.FirstName} ${student.LastName},\n\nWe're excited to inform you that your Demo Form has been successfully submitted on ${new Date(student.ClassDateTime).toLocaleString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })} \n\nTo join the upcoming class, please visit our website:\nrobomind-edu.com\n\nThen, click on the *Join free class* button and use the following code to join:\n*${student.CodeJoinDemo}*\n\nPlease join from your computer so you can participate.\n\nHappy Learning from RoboMind!`;
    
    window.open(`https://api.whatsapp.com/send?phone=${student.MobileNumber}&text=${encodeURIComponent(message)}`, '_blank');
  };

 







 
  const formatForDateTimeInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      return format(date, "dd-MM-yyyy h:mm a");

    } catch {
      return '';
    }
  };


  if (loading) {
    return <div className="sp-student-profile-loading">Loading...</div>;
  }
  
  if (error) return <div className="sp-student-profile-error">Error: {error}</div>;
  if (!student) return <div className="sp-student-profile-not-found">Student not found</div>;

  return (
    <div className="sp-container">
      <div className="sp-header">
        <div className="sp-avatar-container">
          <img 
            src={student.ImageUrl1 || "../../assets/img/student.jpg"} 
            alt={student.FirstName} 
            className="sp-avatar"
          />
        </div>
        
        <div className="sp-profile-content">
          <div className="sp-info-section">
            <h1 className="sp-student-name">
              {student.FirstName} {student.LastName}
              <span className={`sp-student-role ${isDemoStudent ? 'sp-demo-role' : ''}`}>
                {isDemoStudent ? 'Demo Student' : 'Paid Student'}
              </span>
              <span className={`sp-plan-badge ${student.IdGradesPlans === 1 ? 'sp-plan-little' : 'sp-plan-junior'}`}>
                  {student.IdGradesPlans === 1 ? "Little" : "Junior"}
              </span>
            </h1>
            
          </div>
          <p className='sp-plan-badge'>Last Sign In: {formatForDateTimeInput(student.Lastsignin)}</p>
          <p className='sp-plan-badge'>Last Seen: {formatForDateTimeInput(student.LastSeen)}</p>

        </div>
      </div>

      <div className="sp-action-buttons">
        <button 
          className="sp-btn sp-btn-whatsapp"
          onClick={() => window.open(`https://wa.me/${student.MobileNumber}`, '_blank')}
        >
          <i className="fab fa-whatsapp"></i> WhatsApp
        </button>
        {isDemoStudent ? (
          <button 
            className="sp-btn sp-btn-secondary"
            onClick={sendDemoInvitation}
          >
            <i className="fas fa-envelope"></i> Send Demo Invitation
          </button>
        ) : (
          <button 
            className="sp-btn sp-btn-secondary"
            onClick={sendWhatsAppMessage}
          >
            <i className="fas fa-envelope"></i> Send Credentials
          </button>
        )}
        {isDemoStudent && (
          <button 
            className="sp-btn sp-btn-secondary disabled"
            disabled="disabled"
          >
            {updatingToPaid ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Updating...
              </>
            ) : (
              'Update to Paid'
            )}
          </button>
        )}
      </div>

      {!isDemoStudent ? (
        <div className="sp-tabs-container">
          <button 
            className={`sp-tab ${activeTab === 'profileinfo' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('profileinfo')}
          >
            <i className="fas fa-user"></i> Profile Info
          </button>
          <button 
            className={`sp-tab ${activeTab === 'classes' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('classes')}
          >
            <i className="fas fa-chalkboard-teacher"></i> Classes
          </button>
          <button 
            className={`sp-tab ${activeTab === 'assignment' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('assignment')}
          >
            <i className="fas fa-user-tie"></i> Teacher Assignment
          </button>
          <button 
            className={`sp-tab ${activeTab === 'edit' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('edit')}
          >
            <i className="fas fa-user-edit"></i> Edit Info
          </button>
          <button 
            className={`sp-tab ${activeTab === 'reset' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('reset')}
          >
            <i className="fas fa-key"></i> Reset Password
          </button>
          <button 
            className={`sp-tab ${activeTab === 'calendar' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            <i className="fas fa-calendar-alt"></i> Schedule
          </button>
          <button 
            className={`sp-tab ${activeTab === 'calendarslot' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('calendarslot')}
          >
            <i className="fas fa-calendar-check"></i> Schedule Slots
          </button>
          <button 
            className={`sp-tab ${activeTab === 'awaitingpayment' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('awaitingpayment')}
          >
            <i className="fas fa-money-bill-wave"></i> Payments
          </button>
          

          <button 
            className={`sp-tab ${activeTab === 'certificates' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('certificates')}
          >
            <i className="fas fa-certificate"></i> Certificates
          </button>

          <button 
            className={`sp-tab ${activeTab === 'studentptm' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('studentptm')}
          >
            <i className="fas fa-certificate"></i> PTM
          </button>
        </div>
      ) : (
        <div className="sp-tabs-container">
          <button 
            className={`sp-tab ${activeTab === 'profileinfo' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('profileinfo')}
          >
            <i className="fas fa-user"></i> Profile Info
          </button>
          <button 
            className={`sp-tab ${activeTab === 'edit' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('edit')}
          >
            <i className="fas fa-user-edit"></i> Edit Info
          </button>
          <button 
            className={`sp-tab ${activeTab === 'calendar' ? 'sp-tab-active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            <i className="fas fa-calendar-alt"></i> Schedule
          </button>
        </div>
      )}

      <div className="sp-tab-content">
        {activeTab === 'profileinfo' && (
          <div className="sp-profile-info-content">
            <div className="sp-info-grid">
              <div className="sp-info-item">
                <label>Email:</label>
                <span>{student.ParentsEmail}</span>
              </div>
              {!isDemoStudent && (
                <div className="sp-info-item">
                  <label>Username:</label>
                  <span>{student.Username}</span>
                </div>
              )}
              <div className="sp-info-item">
                <label>Mobile:</label>
                <span>{student.MobileNumber}</span>
              </div>
              <div className="sp-info-item">
                <label>Birthday:</label>
                <span>{student.Birthday ? new Date(student.Birthday).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="sp-info-item">
                <label>Age:</label>
                <span>{student.Age}</span>
              </div>
              <div className="sp-info-item">
                <label>Grade:</label>
                <span>{student.Grade}</span>
              </div>
              
              
                <div className="sp-info-item">
                  <label>Code:</label>
                  <span className="sp-demo-code">{student.CodeJoinDemo? student.CodeJoinDemo : "123456aA@"}</span>
                </div>
              
            </div>
          </div>
        )}
        
        {!isDemoStudent ? (
          <>
            {activeTab === 'classes' && <StudentClasses StudentId={id} />}
            {activeTab === 'assignment' && (
              <div className="sp-assignment-section">
                <h3 className="sp-assignment-title">Assign Teacher</h3>
                <div className="sp-assignment-controls">
                  <select disabled
                    value={assignedTeacher || ''}
                    onChange={(e) => setAssignedTeacher(e.target.value)}
                    className="sp-teacher-select"
                  >
                    <option value="">Select Teacher</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.name}
                      </option>
                    ))}
                  </select>
                  
                  <div className="sp-assignment-actions">
                    
                    
                    
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'awaitingpayment' && (
              <div className="sp-assignment-section">
                <div className="sp-assignment-header">
                  <h3 className="sp-assignment-title">Payments</h3>
                  
                </div>
                <div className="sp-assignment-controls">
                  {error && <div className="sp-alert sp-alert-danger">{error}</div>}
                  <div className="table-responsive">
                    <table className="sp-payments-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount (USD)</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.length > 0 ? (
                          payments.map(payment => (
                            <tr key={payment.Id}>
                              <td>
                                {payment.DateTimeOfPayement ? 
                                  new Date(payment.DateTimeOfPayement).toLocaleDateString() : 
                                  'Pending'}
                              </td>
                              <td>${payment.Amount}</td>
                              <td>
                                {payment.Check ? (
                                  <span className="sp-badge sp-badge-success">Paid</span>
                                ) : (
                                  <span className="sp-badge sp-badge-warning">Pending</span>
                                )}
                              </td>
                              <td>
                                <button 
                                  className="sp-btn sp-btn-sm sp-btn-outline"
                                  
                                >
                                  <i className="fas fa-edit"></i> Edit
                                </button>
                                <button 
                                  className="sp-btn sp-btn-sm sp-btn-outline-danger"
                                  
                                >
                                  <i className="fas fa-trash"></i> Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="sp-no-payments">
                              No payment records found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'reset' && <ResetPassword teacherId={id} />}
            {activeTab === 'calendar' && <StudentSchedule studentId={id} studentType={isDemoStudent} />}
            {activeTab === 'calendarslot' && assignedTeacher && (
              <StudentPaidAvailability studentId={id} assignedTeacher={assignedTeacher}/>
            )}
            
            {activeTab === 'certificates' && (
              <Certificates studentId={id} studentType={student.IdGradesPlans} />
            )}
            {activeTab === 'studentptm' && (
              <PTM studentId={id} studentType={student.IdGradesPlans} />
            )}
          </>
        ) : (
          <>
            {activeTab === 'calendar' && <StudentSchedule studentId={id} studentType={isDemoStudent} />}
          </>
        )}
      </div>

    

     
    </div>
  );
};

export default StudentProfile;